<script setup lang="ts">
type IconName =
  | "playground"
  | "kidsroom"
  | "carousel"
  | "trampoline"
  | "iceberg";

interface KidsZoneItem {
  icon: IconName;
  label: string;
  note?: string;
}

interface KidsZonesBlock {
  header: string;
  intro?: string;
  note?: string;
  items: KidsZoneItem[];
}

defineProps<{
  block: KidsZonesBlock;
}>();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-emerald-50/40 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -left-16 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl dark:bg-rose-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-emerald-200/25 blur-3xl dark:bg-emerald-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 pt-2 pb-8 md:pt-4 md:pb-12">
      <header class="mb-6 flex flex-col gap-4 md:mb-8">
        <h2
          class="font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="max-w-4xl font-body text-base leading-relaxed text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div class="grid gap-3 sm:grid-cols-3 md:gap-4">
        <div
          v-for="item in block.items"
          :key="item.icon + item.label"
          class="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm transition-shadow hover:shadow-md md:p-5 dark:border-slate-700 dark:bg-slate-800/50"
        >
          <span
            class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
          >
            <svg
              v-if="item.icon === 'playground'"
              class="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M4 20V10l6-4 4 3 6-5v16" />
              <path d="M4 20h16" />
            </svg>
            <svg
              v-else-if="item.icon === 'kidsroom'"
              class="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M3 10 12 3l9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10Z" />
              <path d="M9 21v-6h6v6" />
            </svg>
            <svg
              v-else-if="item.icon === 'carousel'"
              class="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3v3" />
              <path d="M4 8c2-2 5-3 8-3s6 1 8 3" />
              <path d="M5 20V9M19 20V9M9 20v-8M15 20v-8" />
              <path d="M4 20h16" />
            </svg>
            <svg
              v-else-if="item.icon === 'trampoline'"
              class="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <ellipse cx="12" cy="10" rx="9" ry="3" />
              <path d="M5 12v6M19 12v6M8 11v7M16 11v7" />
            </svg>
            <svg
              v-else
              class="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M3 20h18" />
              <path d="M6 20 12 6l6 14" />
              <path d="M9 15h6" />
            </svg>
          </span>

          <h3
            class="mt-3 font-display text-base italic text-slate-900 md:text-lg dark:text-white"
          >
            {{ item.label }}
          </h3>
          <p
            v-if="item.note"
            class="mt-1 font-body text-xs leading-relaxed text-slate-500 dark:text-slate-400"
          >
            {{ item.note }}
          </p>
        </div>
      </div>

      <p
        v-if="block.note"
        class="mt-4 inline-flex items-start gap-2 font-body text-sm text-slate-600 md:mt-6 dark:text-slate-300"
      >
        <svg
          class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <span>{{ block.note }}</span>
      </p>
    </div>
  </section>
</template>
